import growl from "app/components/common/growl";
import { HealthStatus } from "core/services/copilotService";

type variant = "error" | "success" | "warning";

export interface ErrorResult {
    title: string;
    message: string;
    variant: variant;
}

export function getErrorResult(healthResult: HealthStatus | null): ErrorResult | null {
    if (!healthResult) {
        return {
            title: "Couldn't reach Server",
            message:
                "Striim couldn't connect to the server. Please verify the Striim AI connection URL or ensure Docker is running.",
            variant: "error"
        };
    }

    if (!healthResult.isAuth) {
        return {
            title: "Authentication failed",
            message: "Striim AI authentication failed. Reconfigure Striim AI with valid credentials.",
            variant: "error"
        };
    }

    if (healthResult.status === "unhealthy") {
        return {
            title: "Service unhealthy",
            message: `Striim AI service "${healthResult.service}" is unhealthy.`,
            variant: "error"
        };
    }

    const agent = healthResult.agent;

    if (agent && !agent.ok) {
        return {
            title: "Agent unavailable",
            message: agent.error || `Agent service "${agent.service}" is not reachable or not ok.`,
            variant: "error"
        };
    }

    if (agent && !agent.chat_model_available) {
        return {
            title: "Chat model unavailable",
            message: agent.error || "The configured chat model is not available.",
            variant: "error"
        };
    }

    if (healthResult.status === "degraded") {
        return {
            title: "Service degraded",
            message: `Striim AI service "${healthResult.service}" is degraded.`,
            variant: "error"
        };
    }

    if (agent && agent.knowledgebase_enabled && !agent.embed_model_available) {
        return {
            title: "Embedding model unavailable",
            message: agent.error || "Knowledgebase is enabled but the configured embedding model is not available.",
            variant: "error"
        };
    }

    return null;
}

const CONNECTION_ERROR_SHOWN_KEY = "striimAiConnectionErrorShown";

export function markConnectionErrorShown(): void {
    sessionStorage.setItem(CONNECTION_ERROR_SHOWN_KEY, "true");
}

export function clearConnectionErrorShown(): void {
    sessionStorage.removeItem(CONNECTION_ERROR_SHOWN_KEY);
}

export function wasConnectionErrorShown(): boolean {
    return sessionStorage.getItem(CONNECTION_ERROR_SHOWN_KEY) === "true";
}

export function growlStriimAiConnectionError(errorResult: ErrorResult) {
    if (!errorResult) {
        return;
    }
    if (errorResult.variant == "warning") {
        growl.warning(errorResult.message, errorResult.title);
    }

    if (errorResult.variant == "error") {
        growl.error(errorResult.message, errorResult.title);
    }
}
